var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import classnames from 'classnames';
import omit from 'lodash/omit';
import moment from 'moment';
import React from 'react';
import EdsDatePicker from '@hedtech/react-design-system/core/es/DatePicker';
import Tokens from '../styles/Tokens';
import { createStyles, withStyles } from '../styles/withStyles';
const styles = createStyles({
    '@global': {
        '.CalendarDay.CalendarDay__blocked_calendar': {
            backgroundColor: `${Tokens.colorBrandNeutral100}!important`
        },
        '.DateInput': {
            width: 'calc(100% - 1rem)!important'
        },
        '.SingleDatePickerInput': {
            alignItems: 'center',
            display: 'flex'
        },
        '.SingleDatePickerInput_calendarIcon': {
            marginLeft: '0!important',
            position: 'absolute',
            right: '2px'
        }
    },
    customHighlightingStyle: {
        '& td.CalendarDay.CalendarDay__default:not(.CalendarDay__blocked_calendar):not(.CalendarDay__selected)': {
            '&.CalendarDay__highlighted_calendar': {
                '&:hover': {
                    backgroundColor: Tokens.colorBrandNeutral500,
                    color: Tokens.colorBrandNeutral100
                },
                backgroundColor: Tokens.tangerine200,
                color: Tokens.colorBrandNeutral600
            },
            '&:not(.CalendarDay__highlighted_calendar)': {
                '&:hover': {
                    backgroundColor: Tokens.colorBrandNeutral500,
                    color: Tokens.colorBrandNeutral100
                },
                backgroundColor: Tokens.kiwi200,
                color: Tokens.colorBrandNeutral600
            }
        }
    },
    standaloneWithoutBorder: {
        '& > div': {
            borderColor: 'transparent!important'
        }
    }
});
class DatePicker extends React.Component {
    constructor(props) {
        super(props);
        this.onDateChange = (date) => {
            const { id, onChangeValue } = this.props;
            this.setState({
                date: date
            });
            if (onChangeValue) {
                if (date && date.date) {
                    onChangeValue(date.format(this.format), id, true);
                }
                else if (this.inputDate) {
                    onChangeValue(this.inputDate.value, id, false);
                }
                else {
                    onChangeValue('', id, true);
                }
            }
        };
        this.onFocusChange = ({ focused }) => {
            this.setState({
                focused: focused
            });
        };
        this.isOutsideRange = (day) => {
            const { rangeEnd, rangeEndPeriod, rangeStart, rangeStartPeriod } = this.props;
            if (rangeEnd !== undefined && rangeEndPeriod !== undefined && rangeStart === undefined && rangeStartPeriod === undefined) {
                return day.isBefore(moment().subtract(100, 'years')) || day.isAfter(moment().add(rangeEnd, rangeEndPeriod));
            }
            if (rangeEnd === undefined && rangeEndPeriod === undefined && rangeStart !== undefined && rangeStartPeriod !== undefined) {
                return day.isBefore(moment().subtract(rangeStart, rangeStartPeriod)) || day.isAfter(moment().add(100, 'years'));
            }
            if (rangeEnd !== undefined && rangeEndPeriod !== undefined && rangeStart !== undefined && rangeStartPeriod !== undefined) {
                return day.isBefore(moment().subtract(rangeStart, rangeStartPeriod)) || day.isAfter(moment().add(rangeEnd, rangeEndPeriod));
            }
            return day.isBefore(moment().subtract(100, 'years')) || day.isAfter(moment().add(100, 'years'));
        };
        this.initialVisibleMonth = () => {
            const { date } = this.state;
            if (date) {
                return date;
            }
            return moment();
        };
        this.isDayBlocked = (day) => {
            const { blockUnusedHighlightedDays, daysBlocked, daysHighlighted, daysNotHighlighted, daysOfTheWeekBlocked } = this.props;
            let isBlocked = false;
            if (blockUnusedHighlightedDays) {
                let isHighlighted = false;
                if (daysHighlighted && daysHighlighted.length > 0) {
                    daysHighlighted.forEach(dayToHighlight => {
                        if (!isHighlighted) {
                            isHighlighted = day.isSame(moment(dayToHighlight, this.format), 'day');
                        }
                    });
                }
                if (daysNotHighlighted && daysNotHighlighted.length > 0) {
                    daysNotHighlighted.forEach(dayToNotHighlight => {
                        if (!isHighlighted) {
                            isHighlighted = day.isSame(moment(dayToNotHighlight, this.format), 'day');
                        }
                    });
                }
                isBlocked = !isHighlighted;
            }
            if (!isBlocked && daysBlocked && daysBlocked.length > 0) {
                daysBlocked.forEach(dayToBlock => {
                    if (!isBlocked) {
                        isBlocked = day.isSame(moment(dayToBlock, this.format), 'day');
                    }
                });
            }
            if (!isBlocked && daysOfTheWeekBlocked && daysOfTheWeekBlocked.length > 0) {
                daysOfTheWeekBlocked.forEach(dayToBlock => {
                    if (!isBlocked) {
                        isBlocked = moment.weekdays(day.weekday()) === dayToBlock;
                    }
                });
            }
            return isBlocked;
        };
        this.isDayHighlighted = (day) => {
            const { daysHighlighted } = this.props;
            let isHighlighted = false;
            if (daysHighlighted && daysHighlighted.length > 0) {
                daysHighlighted.forEach(dayToHighlight => {
                    if (!isHighlighted) {
                        isHighlighted = day.isSame(moment(dayToHighlight, this.format), 'day');
                    }
                });
            }
            return isHighlighted;
        };
        this.onChangeBeforeYears = () => {
            const { yearsBefore } = this.props;
            if (yearsBefore) {
                return moment().year() - yearsBefore;
            }
            return moment().year() - 100;
        };
        this.onChangeAfterYears = () => {
            const { yearsAfter } = this.props;
            if (yearsAfter) {
                return moment().year() + yearsAfter;
            }
            return moment().year() + 20;
        };
        this.format = 'YYYY-MM-DD';
        moment.locale(this.props.culture);
        this.state = this.getInitialState(props.value, props.variant);
    }
    getInitialState(value, variant) {
        return {
            date: value ? moment(value, this.format) : null,
            focused: variant && variant === 'standalone' ? true : false
        };
    }
    onPaste(event) {
        event.stopPropagation();
        event.cancelBubble = true;
        event.preventDefault();
        event.returnValue = false;
        return false;
    }
    componentDidMount() {
        this.inputDate =
            document.getElementById(this.props.id);
        if (this.inputDate) {
            this.inputDate.addEventListener('paste', this.onPaste);
        }
    }
    render() {
        const _a = this.props, { classes, className, customHighlightingStyle, displayFormat, monthSelectionVariant, variant, withoutBorder } = _a, rest = __rest(_a, ["classes", "className", "customHighlightingStyle", "displayFormat", "monthSelectionVariant", "variant", "withoutBorder"]);
        const { date, focused } = this.state;
        let classNameOverrides = className;
        if (variant && variant === 'standalone' && withoutBorder) {
            classNameOverrides = classnames(classNameOverrides, classes.standaloneWithoutBorder);
        }
        if (customHighlightingStyle) {
            classNameOverrides = classnames(classNameOverrides, classes.customHighlightingStyle);
        }
        return (React.createElement(EdsDatePicker, Object.assign({ classes: omit(classes, ['customHighlightingStyle', 'standaloneWithoutBorder']), className: classNameOverrides, date: date, displayFormat: displayFormat.toUpperCase(), endYear: this.onChangeAfterYears(), focused: focused, fullWidth: true, monthSelectionVariant: monthSelectionVariant ? monthSelectionVariant : 'month-year', startYear: this.onChangeBeforeYears(), variant: variant, initialVisibleMonth: this.initialVisibleMonth, isDayBlocked: this.isDayBlocked, isDayHighlighted: this.isDayHighlighted, isOutsideRange: this.isOutsideRange, onDateChange: this.onDateChange, onFocusChange: this.onFocusChange }, omit(rest, [
            'blockUnusedHighlightedDays',
            'culture',
            'customHighlightingStyle',
            'dayBlocked',
            'daysHighlighted',
            'daysNotHighlighted',
            'isInTable',
            'key',
            'monthsFromToday',
            'monthsVisible',
            'rangeEnd',
            'rangeEndPeriod',
            'rangeStart',
            'rangeStartPeriod',
            'value',
            'onChangeValue'
        ]))));
    }
}
export default withStyles(styles)(DatePicker);
